<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Table from '../../Table.svelte';
  import SearchInput from '../../SearchInput.svelte';
  import Pagination from '../../Pagination.svelte';
  import Button from '../../Button.svelte';
  import AsyncSearchableDropdown from '../../AsyncSearchableDropdown.svelte';
  import { charitiesSearchService } from '../../../lib/api/admin/charities-search.service';
  import employersSearchService from '../../../lib/api/admin/employers-search.service';
  import PaymentsService, { type IncomingPayment } from '../../../lib/api/admin/payments.service';
  import { createPreferencesManager } from '../../../lib/utils/preferences';
  import LoadingSkeleton from '../../LoadingSkeleton.svelte';
  import EmptyState from '../../EmptyState.svelte';
  import ErrorState from '../../ErrorState.svelte';
  import toast from 'svelte-french-toast';

  // Preferences manager
  const preferences = createPreferencesManager('incoming-payments');

  // State
  let isLoading = true;
  let error = '';
  let payments: IncomingPayment[] = [];
  let totalPages = 1;
  let totalItems = 0;

  // Filters - initialize from preferences
  const savedFilters = preferences.getFilters();
  let searchPhrase = savedFilters.searchPhrase || '';
  let selectedEmployer = savedFilters.selectedEmployer || '';
  let selectedCharity = savedFilters.selectedCharity || '';

  // Pagination - initialize from preferences
  let currentPage = preferences.getCurrentPage() || 1;
  let itemsPerPage = preferences.getPageSize() || 20;

  // Search functions for async dropdowns
  async function searchEmployers(query: string): Promise<Array<{uuid: string, name: string}>> {
    try {
      if (!query.trim()) return [];
      return await employersSearchService.searchEmployers(query, 10);
    } catch (err) {
      console.error('Error searching employers:', err);
      return [];
    }
  }

  async function getEmployerLabel(uuid: string): Promise<string | null> {
    try {
      return await employersSearchService.getEmployerName(uuid);
    } catch (err) {
      console.error('Error getting employer name:', err);
      return null;
    }
  }

  async function searchCharities(query: string): Promise<Array<{uuid: string, name: string}>> {
    try {
      if (!query.trim()) return [];
      return await charitiesSearchService.searchCharities(query, 10);
    } catch (err) {
      console.error('Error searching charities:', err);
      return [];
    }
  }

  async function getCharityLabel(uuid: string): Promise<string | null> {
    try {
      return await charitiesSearchService.getCharityName(uuid);
    } catch (err) {
      console.error('Error getting charity name:', err);
      return null;
    }
  }

  function formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  }

  const tableColumns = [
    {
      key: 'employer',
      label: 'Employer',
      width: '35%',
      render: (value: any, row: IncomingPayment) => row.employer.name
    },
    {
      key: 'donation_count',
      label: 'Donations',
      width: '20%',
      align: 'center',
      render: (value: number | undefined) => value ?? '—'
    },
    {
      key: 'amount',
      label: 'Amount',
      width: '25%',
      render: (value: number) => formatCurrency(value)
    },
    {
      key: 'type',
      label: 'Status',
      width: '20%',
      align: 'center',
      render: () =>
        '<span class="status-badge status-pending">Awaiting Employer Payment</span>'
    }
  ];

  async function loadPayments() {
    try {
      isLoading = true;
      error = '';

      const filters: any = {
        page: currentPage,
        per_page: itemsPerPage
      };

      if (searchPhrase) filters.phrase = searchPhrase;
      if (selectedEmployer) filters.employer_uuid = selectedEmployer;
      if (selectedCharity) filters.charity_uuid = selectedCharity;

      const response = await PaymentsService.getIncomingPayments(filters);

      if (response.success && response.data) {
        payments = Array.isArray(response.data) ? response.data : (response.data.items || []);
        totalPages = response.paging?.last_page || 1;
        totalItems = response.paging?.total || 0;
      } else {
        error = response.message || 'Failed to load payments';
        toast.error(error);
      }
    } catch (err: any) {
      error = err.message || 'Failed to load payments';
      toast.error(error);
    } finally {
      isLoading = false;
    }
  }

  function handleSearch(event: CustomEvent<string>) {
    searchPhrase = event.detail;
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { searchPhrase, selectedEmployer, selectedCharity } });
    loadPayments();
  }

  function handleEmployerChange(value: string) {
    selectedEmployer = value;
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { searchPhrase, selectedEmployer, selectedCharity } });
    loadPayments();
  }

  function handleCharityChange(value: string) {
    selectedCharity = value;
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { searchPhrase, selectedEmployer, selectedCharity } });
    loadPayments();
  }

  function handleResetFilters() {
    searchPhrase = '';
    selectedEmployer = '';
    selectedCharity = '';
    currentPage = 1;
    preferences.reset();
    loadPayments();
  }

  function handlePageChange(event: CustomEvent<{ page: number }>) {
    currentPage = event.detail.page;
    preferences.update({ currentPage });
    loadPayments();
  }

  function handleItemsPerPageChange(event: CustomEvent<{ value: number }>) {
    itemsPerPage = event.detail.value;
    currentPage = 1;
    preferences.update({ pageSize: itemsPerPage, currentPage: 1 });
    loadPayments();
  }

  function handleRowClick(row: IncomingPayment) {
    push(`/admin/payments/incoming/${row.uuid}`);
  }

  // Watch for filter changes and save preferences
  $: if (searchPhrase !== undefined || selectedEmployer !== undefined || selectedCharity !== undefined) {
    preferences.updateFilters({ searchPhrase, selectedEmployer, selectedCharity });
  }

  // Watch for page size changes
  $: if (itemsPerPage !== undefined) {
    preferences.update({ pageSize: itemsPerPage });
  }

  // Watch for page changes
  $: if (currentPage !== undefined) {
    preferences.update({ currentPage });
  }

  onMount(() => {
    loadPayments();
  });
</script>

<div class="payments-list-page">
  <div class="page-header">
    <p class="subtitle">Approved donations awaiting payment from employers</p>
  </div>

  <!-- Filters Section -->
  <div class="filters-section">
    <div class="filters-left">
      <div class="filter-item">
        <SearchInput
          placeholder="Search by employer..."
          bind:value={searchPhrase}
          on:search={handleSearch}
          width="100%"
        />
      </div>

      <div class="filter-item">
        <AsyncSearchableDropdown
          placeholder="Search employer..."
          selectedValue={selectedEmployer}
          searchFunction={searchEmployers}
          getSelectedLabel={getEmployerLabel}
          onSelectionChange={handleEmployerChange}
          debounceMs={300}
        />
      </div>

      <div class="filter-item">
        <AsyncSearchableDropdown
          placeholder="Search charity..."
          selectedValue={selectedCharity}
          searchFunction={searchCharities}
          getSelectedLabel={getCharityLabel}
          onSelectionChange={handleCharityChange}
          debounceMs={300}
        />
      </div>
    </div>
    
    <div class="filters-right">
      <Button variant="secondary" on:click={handleResetFilters}>
        Reset
      </Button>
    </div>
  </div>

  <!-- Payments Table Section -->
  <div class="table-section">
    {#if isLoading}
      <div class="loading-wrap">
        <LoadingSkeleton lines={10} height="2.5rem" />
      </div>
    {:else if error}
      <ErrorState
        message="Failed to load payments"
        description={error}
        onRetry={loadPayments}
      />
    {:else if payments.length === 0}
      <EmptyState
        message="No payments awaiting from employers"
        description="Approved donations that employers have not yet paid will appear here."
        actionLabel="Reset Filters"
        onAction={handleResetFilters}
      />
    {:else}
      <Table
        columns={tableColumns}
        data={payments}
        onRowClick={handleRowClick}
      />

      {#if totalPages > 1}
        <div class="pagination-container">
          <Pagination
            {currentPage}
            {totalPages}
            on:pageChange={handlePageChange}
          />
        </div>
      {/if}
    {/if}
  </div>
</div>

<style>
  .page-header {
    margin-bottom: 1.5rem;
  }

  .subtitle {
    margin: 0;
    font-size: 0.875rem;
    color: #6b7280;
    line-height: 1.5;
  }

  .filters-section {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-bottom: 1rem;
    width: 100%;
  }

  .filters-left {
    display: flex;
    flex: 1;
    gap: 0.75rem;
    flex-wrap: wrap;
    align-items: flex-start;
    min-width: 280px;
  }

  .filter-item {
    min-width: 0;
  }

  .filter-item:first-child {
    flex: 1;
    min-width: 280px;
  }

  .filters-right {
    display: flex;
    gap: 0.75rem;
    flex-shrink: 0;
  }

  .table-section {
    background: white;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    overflow: hidden;
  }

  .loading-wrap {
    padding: 2rem;
    min-height: 320px;
  }

  .pagination-container {
    padding: 1rem 1.5rem;
    border-top: 1px solid #f3f4f6;
  }

  :global(.status-badge) {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
  }

  /* Canonical palette (TONE_PALETTE in src/lib/donationLifecycle.ts):
     Paid by Employer = cyan (employer-paid, mid-pipeline); Awaiting = amber. */
  :global(.status-badge.status-paid) {
    background-color: #cffafe;
    color: #155e75;
  }

  :global(.status-badge.status-pending) {
    background-color: #fef3c7;
    color: #92400e;
  }

  :global(.text-muted) {
    color: #9ca3af;
    font-style: italic;
  }
</style>

